import React, { useState, useEffect } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { Link } from 'react-router-dom'
import {
  DocumentTextIcon,
  CloudArrowUpIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  TrashIcon,
  XMarkIcon,
  ArrowsPointingOutIcon,
} from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { documentsAPI } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import useLanguage from '../../hooks/useLanguage'
import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import Badge from '../../components/ui/Badge'
import toast from 'react-hot-toast'

const DocumentsPage = () => {
  const { t } = useTranslation('documents')
  const { locale } = useLanguage()
  const queryClient = useQueryClient()
  const { token } = useAuth()
  const [searchTerm, setSearchTerm] = useState('')
  const [thumbnailUrls, setThumbnailUrls] = useState({})
  const [lightboxUrl, setLightboxUrl] = useState(null)
  const [filters, setFilters] = useState({
    category: '',
    ocr_status: '',
    date_from: '',
    date_to: ''
  })

  const { data: documentsData, isLoading, error } = useQuery(
    ['documents', { search: searchTerm, ...filters }],
    () => documentsAPI.getDocuments({ 
      search: searchTerm || undefined,
      ...Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== '')
      )
    }),
    {
      keepPreviousData: true,
    }
  )

  const getStatusBadge = (status, ocrResult = null) => {
    const variants = {
      pending: 'gray',
      processing: 'warning',
      completed: 'success',
      failed: 'error'
    }

    const labels = {
      pending: t('common:status.pending'),
      processing: t('common:status.processing'),
      completed: t('common:status.completed'),
      failed: t('common:status.failed')
    }

    return (
      <div className="flex flex-col items-end">
        <Badge variant={variants[status] || 'gray'} size="xs">
          {labels[status] || status}
        </Badge>
        {ocrResult && ocrResult.processing_time && status === 'completed' && (
          <div className="text-xs text-[var(--color-text-muted)] mt-1">
            {(() => {
              const t = parseFloat(ocrResult.processing_time);
              if (isNaN(t)) return '—';
              if (t < 60) return `${t.toFixed(1)}s`;
              return `${Math.floor(t / 60)}m ${Math.round(t % 60)}s`;
            })()} • {ocrResult.engine}
          </div>
        )}
      </div>
    )
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm(t('confirmDelete'))) {
      return
    }

    try {
      await documentsAPI.deleteDocument(documentId)
      queryClient.invalidateQueries('documents')
      queryClient.invalidateQueries(['expenses'])
      queryClient.invalidateQueries('dashboard')
      toast.success(t('deleteSuccess'))
    } catch (error) {
      toast.error(t('deleteError'))
    }
  }

  const fetchThumbnail = async (documentId) => {
    if (thumbnailUrls[documentId]) {
      return thumbnailUrls[documentId]
    }

    try {
      const baseUrl = import.meta.env.VITE_API_BASE_URL || '/api'
      const response = await fetch(
        `${baseUrl}/documents/${documentId}/thumbnail`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      )

      if (response.ok) {
        const blob = await response.blob()
        const url = URL.createObjectURL(blob)
        setThumbnailUrls(prev => ({ ...prev, [documentId]: url }))
        return url
      }
    } catch (error) {
      console.error('Error fetching thumbnail:', error)
    }

    return null
  }

  const openFullImage = async (documentId) => {
    try {
      const baseUrl = import.meta.env.VITE_API_BASE_URL || '/api'
      const response = await fetch(`${baseUrl}/documents/${documentId}/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (response.ok) {
        const blob = await response.blob()
        setLightboxUrl(URL.createObjectURL(blob))
      }
    } catch (error) {
      console.error('Error fetching full image:', error)
    }
  }

  const closeLightbox = () => {
    if (lightboxUrl) URL.revokeObjectURL(lightboxUrl)
    setLightboxUrl(null)
  }

  // Pobierz thumbnails dla wszystkich dokumentów
  useEffect(() => {
    if (documentsData?.data?.documents && token) {
      documentsData.data.documents.forEach(document => {
        fetchThumbnail(document.id)
      })
    }
  }, [documentsData?.data?.documents, token])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text={t('loading')} />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-error-600">{t('error')}</p>
      </div>
    )
  }

  const { documents = [], pagination } = documentsData?.data || {}

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-sm text-[var(--color-text-muted)]">
            {t('subtitle')}
          </p>
        </div>
        <div className="mt-4 flex md:ml-4 md:mt-0">
          <Button
            as={Link}
            to="/dashboard/documents/upload"
            variant="primary"
            leftIcon={<CloudArrowUpIcon className="h-4 w-4" />}
          >
            {t('addDocument')}
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <Card>
        <Card.Body>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {/* Search */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-[var(--color-text-faint)]" />
              </div>
              <input
                type="text"
                placeholder={t('searchPlaceholder')}
                className="form-input pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Status Filter */}
            <select
              className="form-select"
              value={filters.ocr_status}
              onChange={(e) => setFilters({ ...filters, ocr_status: e.target.value })}
            >
              <option value="">{t('allStatuses')}</option>
              <option value="pending">{t('common:status.pending')}</option>
              <option value="processing">{t('common:status.processing')}</option>
              <option value="completed">{t('common:status.completed')}</option>
              <option value="failed">{t('common:status.failed')}</option>
            </select>

            {/* Date From */}
            <input
              type="date"
              className="form-input"
              value={filters.date_from}
              onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
            />

            {/* Date To */}
            <input
              type="date"
              className="form-input"
              value={filters.date_to}
              onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
            />
          </div>
        </Card.Body>
      </Card>

      {/* Documents List */}
      {documents.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {documents.map((document) => (
            <Card key={document.id} hover>
              <Card.Body className="p-3">
                <div className="space-y-2">
                  {/* Thumbnail */}
                  <div className="bg-[var(--color-table-header)] rounded-md overflow-hidden relative group">
                    {thumbnailUrls[document.id] ? (
                      <>
                        <img
                          src={thumbnailUrls[document.id]}
                          alt={document.title}
                          className="w-full h-32 object-cover cursor-pointer"
                          onClick={() => openFullImage(document.id)}
                          onError={(e) => {
                            e.target.style.display = 'none'
                            e.target.nextElementSibling.style.display = 'flex'
                          }}
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center pointer-events-none">
                          <ArrowsPointingOutIcon className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                        <div className="w-full h-32 bg-[var(--color-table-header)] flex items-center justify-center" style={{display: 'none'}}>
                          <svg className="w-10 h-10 text-[var(--color-text-faint)]" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-32 bg-[var(--color-table-header)] flex items-center justify-center">
                        <svg className="w-10 h-10 text-[var(--color-text-faint)]" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>

                  {/* Document Info */}
                  <div className="space-y-1.5">
                    <Link
                      to={`/documents/${document.id}`}
                      className="text-sm font-medium text-[var(--color-text)] hover:text-[#167e75] block truncate"
                    >
                      {document.title}
                    </Link>

                    {/* Category + Status */}
                    <div className="flex items-center justify-between">
                      {document.category ? (
                        <Badge variant="secondary" size="xs">
                          {document.category.name}
                        </Badge>
                      ) : <span />}
                      {getStatusBadge(document.ocr_status, document.ocrResult)}
                    </div>

                    {/* Date + Size */}
                    <div className="flex justify-between text-[10px] text-[var(--color-text-muted)]">
                      <span>
                        {document.createdAt ?
                          new Date(document.createdAt).toLocaleDateString(locale) :
                          t('common:noDate')
                        }
                      </span>
                      <span>{formatFileSize(document.file_size)}</span>
                    </div>

                    {/* Actions */}
                    <div className="flex space-x-1.5 pt-1">
                      <Link
                        to={`/documents/${document.id}`}
                        className="flex-1 inline-flex items-center justify-center px-2 py-1.5 text-xs font-medium border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] hover:bg-[var(--color-table-header)] rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1f9f93]"
                      >
                        {t('common:view')}
                      </Link>
                      <button
                        onClick={() => handleDeleteDocument(document.id)}
                        className="inline-flex items-center justify-center px-2 py-1.5 text-xs font-medium border border-red-300 bg-[var(--color-surface)] text-red-700 hover:bg-red-50 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        <TrashIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          ))}

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-[var(--color-text)]">
                {t('pagination', { page: pagination.page, pages: pagination.pages, total: pagination.total })}
              </p>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page === 1}
                >
                  {t('previous')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page === pagination.pages}
                >
                  {t('next')}
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <Card>
          <Card.Body>
            <div className="text-center py-12">
              <DocumentTextIcon className="mx-auto h-12 w-12 text-[var(--color-text-faint)]" />
              <h3 className="mt-2 text-sm font-medium text-[var(--color-text)]">
                {t('noDocuments')}
              </h3>
              <p className="mt-1 text-sm text-[var(--color-text-muted)]">
                {t('noDocumentsHint')}
              </p>
              <div className="mt-6">
                <Button
                  as={Link}
                  to="/dashboard/documents/upload"
                  variant="primary"
                >
                  {t('addDocument')}
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Lightbox */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
          >
            <XMarkIcon className="h-6 w-6 text-white" />
          </button>
          <img
            src={lightboxUrl}
            alt={t('preview')}
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  )
}

export default DocumentsPage
